import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAsyncCallback } from 'react-async-hook';
import useDeepCompareEffect from 'use-deep-compare-effect';

import { investorApiClient } from 'api';
import { InvestmentWithDealDto, ShareStatus } from 'api/investor-client';
import { useCurrencySelection } from 'providers/currency-selection';
import {
  InvestmentStatusFilterOption,
  PrimaryInvestmentFilterModal,
  PrimaryInvestmentFilterPayload,
  PrimaryInvestmentFilterValues,
  primaryInvestmentFiltersHelper,
} from 'components/filters/primary-investment';
import { ALL_CURRENCIES_VALUE } from 'components/filters/constants';
import { useQuery, useLoadMoreController } from 'hooks';
import { downloadFile, retrieveFileNameFromHeader } from 'utils';
import { EndOfTableMessage, ScrollToTopButton, TableLayout } from 'components';

import { TableFilters } from '../components';
import { PrimaryInvestmentHeader } from './PrimaryInvestmentHeader';
import { PrimaryInvestmentRecord, PrimaryInvestmentRecordSkeleton } from './PrimaryInvestmentRecord';
import { SellModal } from './SellModal';

interface Props {
  wrapperRef?: HTMLElement | null;
  tableLayout: boolean;
}

type TabQuery = {
  currency?: string[];
  shareStatus?: InvestmentStatusFilterOption[];
} & Record<string, unknown>;

const normalizeStringArray = (value: unknown): string[] | undefined => {
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string' && v.length > 0);
  if (typeof value === 'string' && value.length > 0) return [value];
  return undefined;
};

const normalizeShareStatus = (value: unknown): InvestmentStatusFilterOption[] | undefined => {
  if (!value) return undefined;
  const arr = Array.isArray(value) ? value : [value];
  return arr.filter((v): v is InvestmentStatusFilterOption => typeof v === 'string');
};

const toTabQuery = (parsedQuery: Record<string, unknown>): TabQuery => ({
  ...parsedQuery,
  currency: normalizeStringArray(parsedQuery.currency),
  shareStatus: normalizeShareStatus(parsedQuery.shareStatus),
});

const resolveCurrencyFilter = (
  queryCurrency: string[] | undefined,
  selectedCurrency: string | undefined
): string[] | undefined => {
  if (queryCurrency?.includes(ALL_CURRENCIES_VALUE)) return undefined;
  if (queryCurrency && queryCurrency.length > 0) return queryCurrency;
  return selectedCurrency ? [selectedCurrency] : undefined;
};

export const PrimaryMarketTab = ({ wrapperRef, tableLayout }: Props) => {
  const { parsedQuery, push } = useQuery();
  const { selectedCurrency, currencyOptions } = useCurrencySelection();

  const tabQuery = useMemo(() => toTabQuery(parsedQuery as Record<string, unknown>), [parsedQuery]);

  const [data, setData] = useState<InvestmentWithDealDto[]>([]);
  const [continuationToken, setContinuationToken] = useState<string | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [shareToSell, setShareToSell] = useState<InvestmentWithDealDto | null>(null);
  const fetchVersionRef = useRef(0);
  const previousSelectedCurrency = useRef<string | undefined>();
  useEffect(() => {
    if (!selectedCurrency) {
      return;
    }

    const didContextChange = previousSelectedCurrency.current !== selectedCurrency;
    const hasCurrencyFilter = (tabQuery.currency?.length ?? 0) > 0;
    const shouldUpdate = didContextChange || !hasCurrencyFilter;

    if (shouldUpdate && !(tabQuery.currency?.length === 1 && tabQuery.currency[0] === selectedCurrency)) {
      push({
        ...tabQuery,
        currency: [selectedCurrency],
      });
    }
    previousSelectedCurrency.current = selectedCurrency;
    }, [push, selectedCurrency, tabQuery]);

  const fetchInvestments = useAsyncCallback(async (query: TabQuery, fromBeginning?: boolean) => {
    if (!fromBeginning && continuationToken === 'EOF') return;

    const isUnpaid = query.shareStatus?.includes(InvestmentStatusFilterOption.Unpaid) ?? false;

    if (fromBeginning) {
      fetchVersionRef.current += 1;
      setData([]);
      setContinuationToken(null);
      setTotal(null);
    }
    const fetchVersion = fetchVersionRef.current;
    const fetchFunc = isUnpaid
      ? investorApiClient.investors.investorApiControllerGetCommitmentsByContinuationToken
      : investorApiClient.investors.investorApiControllerGetInvestmentsByContinuationToken;

    const currency = resolveCurrencyFilter(query.currency, selectedCurrency);

    const { currency: _currency, shareStatus: _shareStatus, ...rest } = query;

    const params = {
      ...rest,
      currency,
      shareStatus: isUnpaid ? undefined : (_shareStatus as unknown as ShareStatus[] | undefined),
      continuationToken: fromBeginning ? undefined : continuationToken ?? undefined,
    };

    const { data: resp } = await fetchFunc(params as any);

    if (fetchVersion !== fetchVersionRef.current) {
      return;
    }
    setData((state) => (fromBeginning ? resp.data : state.concat(resp.data)));
    setContinuationToken(resp.continuationToken ?? null);
    setTotal((state) => (typeof state === 'number' ? state : resp.total));
  });

  const exportInvestments = useAsyncCallback(async (filters?: PrimaryInvestmentFilterPayload) => {
    const currency = resolveCurrencyFilter(filters?.currency, selectedCurrency);

    const { data: blob, headers } = await investorApiClient.investors.investorApiControllerDownloadInvestments(
      {
        unpaidSeparate: true,
        currency,
      } as any,
      { format: 'blob' }
    );

    downloadFile(blob as unknown as string, retrieveFileNameFromHeader(headers));
  });

  const handleSellModalOpen = useCallback((share: InvestmentWithDealDto) => {
    setShareToSell(share);
    setIsModalOpen(true);
  }, []);

  const handleSellModalClose = useCallback(() => {
    setIsModalOpen(false);
    setTimeout(() => setShareToSell(null), 500);
  }, []);

  useDeepCompareEffect(() => {
    fetchInvestments.execute(tabQuery, true);
  }, [tabQuery, selectedCurrency]);

  useLoadMoreController({
    wrapperRef,
    loading: fetchInvestments.loading,
    continuationToken,
    fetch: fetchInvestments.execute,
    query: tabQuery,
  });

  return (
    <>
      <TableFilters<PrimaryInvestmentFilterPayload, PrimaryInvestmentFilterValues>
        components={{
          TableHeader: PrimaryInvestmentHeader,
          AllFiltersModal: PrimaryInvestmentFilterModal,
        }}
        helpers={primaryInvestmentFiltersHelper}
        initialFilters={tabQuery}
        resetValues={{
          currency: selectedCurrency ? [selectedCurrency] : [],
          status: InvestmentStatusFilterOption.All,
        }}
        currencyOptions={currencyOptions}
        contextCurrency={selectedCurrency}
        onFiltersChange={push}
        onExport={exportInvestments.execute}
        total={total}
        loading={fetchInvestments.loading}
        tableLayout={tableLayout}
      />

      <TableLayout
        components={{
          Record: PrimaryInvestmentRecord,
          RecordSkeleton: PrimaryInvestmentRecordSkeleton,
        }}
        data={data}
        dataLoading={fetchInvestments.loading}
        tableLayout={tableLayout}
        onSell={handleSellModalOpen}
      />

      {continuationToken === 'EOF' && <EndOfTableMessage />}
      <ScrollToTopButton wrapperRef={wrapperRef} />
      <SellModal
        isOpen={isModalOpen}
        onClose={handleSellModalClose}
        share={shareToSell}
        onReload={() => fetchInvestments.execute(tabQuery, true)}
      />
    </>
  );
};
